import { useEffect, useState } from 'preact/hooks';
import { useI18n } from '../i18n';
import { RequireAdmin } from '../components/RequireAdmin';
import { useAuth } from '../lib/auth';
import {
  backfillTestTournament,
  bootstrapSync,
  getAdminSettings,
  linkEuropeanFinals,
  putAdminSettings,
  refreshFixtures,
  refreshTestTournamentFixtures,
  reresolveBracket,
  seedTestTournament,
  syncTestTournamentTick,
  syncUpcoming,
  type AppSettings,
  type BackfillTestTournamentResult,
  type BootstrapResult,
  type LinkEuropeanFinalsResult,
  type RefreshSummary,
  type ReresolveBracketResult,
  type SeedTestTournamentResult,
  type SyncUpcomingResult,
  type TestTournamentTickResult,
} from '../lib/admin';

export function AdminHome() {
  const { localePath } = useI18n();
  const { user, signOut } = useAuth();

  return (
    <RequireAdmin>
      <div class="admin-page">
        <div class="admin-shell">
          <header class="admin-header">
            <h1>BeerCup admin</h1>
            <div class="admin-who">
              <span>{user?.email}</span>
              <a class="admin-link" href={localePath('/play')}>Go to game →</a>
              <button class="admin-link" onClick={() => signOut()}>Sign out</button>
            </div>
          </header>

          <p class="admin-lede">Pre-launch operator console. Manual control + override for everything.</p>

          <div class="admin-grid">
            <a class="admin-card" href={localePath('/admin/matches')}>
              <h2>Match results</h2>
              <p>Enter final scores. Runs the scoring engine.</p>
              <span class="admin-card-status">Live</span>
            </a>
            <a class="admin-card" href={localePath('/admin/partners')}>
              <h2>Partners & sponsors</h2>
              <p>Bars, pubs, breweries donating beer. Tier + logo + URL.</p>
              <span class="admin-card-status">Live</span>
            </a>
            <a class="admin-card" href={localePath('/admin/tournament')}>
              <h2>Prize counter</h2>
              <p>Total litres in play. Surfaces on landing + app.</p>
              <span class="admin-card-status">Live</span>
            </a>
            <a class="admin-card" href={localePath('/admin/users')}>
              <h2>Users</h2>
              <p>Browse, filter and edit signed-up accounts.</p>
              <span class="admin-card-status">Live</span>
            </a>
            <a class="admin-card" href={localePath('/partner')}>
              <h2>Partner self-service (preview)</h2>
              <p>The page partner-role users see to edit their venue + redemption code.</p>
              <span class="admin-card-status">Live</span>
            </a>
            <a class="admin-card" href={localePath('/admin/articles')}>
              <h2>Articles</h2>
              <p>Prize catalog — drinks, experiences, tickets, gadgets. Authored by partners or BeerCup.</p>
              <span class="admin-card-status">Live</span>
            </a>
            <a class="admin-card" href={localePath('/admin/prizes')}>
              <h2>Leaderboard prizes</h2>
              <p>Assign articles to positions on the global + per-canton leaderboards.</p>
              <span class="admin-card-status">Live</span>
            </a>
            <a class="admin-card" href={localePath('/admin/winners')}>
              <h2>Winners</h2>
              <p>End-of-tournament payout list — top finishers, their prize, and a ready-to-send email in their language asking for the details we need to hand it over.</p>
              <span class="admin-card-status">Live</span>
            </a>
            <a class="admin-card" href={localePath('/admin/leaderboard')}>
              <h2>Leaderboard</h2>
              <p>Live ranking across Switzerland or any canton, for any tournament. Rescore all done matches or refresh the rank cache.</p>
              <span class="admin-card-status">Live</span>
            </a>
            <a class="admin-card" href={localePath('/admin/rules')}>
              <h2>Game rules</h2>
              <p>Additive scoring weights for the global BeerCup board. Per-league overrides are set at league creation in the app.</p>
              <span class="admin-card-status">Live</span>
            </a>
            <a class="admin-card" href={localePath('/admin/leagues')}>
              <h2>Leagues</h2>
              <p>Every personal league across all users — search, edit rules (support override), or delete.</p>
              <span class="admin-card-status">Live</span>
            </a>
            <a class="admin-card" href={localePath('/admin/waitlist')}>
              <h2>Waitlist</h2>
              <p>Pre-launch email signups from the landing + store modal. Filter, browse, export CSV.</p>
              <span class="admin-card-status">Live</span>
            </a>
            <a class="admin-card" href={localePath('/admin/newsletter')}>
              <h2>Newsletter</h2>
              <p>Compose a branded email with live preview, test to specific addresses, then blast the waitlist and/or users. One-click unsubscribe built in.</p>
              <span class="admin-card-status">Live</span>
            </a>
            <a class="admin-card" href={localePath('/admin/broadcasts')}>
              <h2>Broadcasts</h2>
              <p>In-app popup message to every user — announcements or update nags with a minimum-version gate. One active at a time, full history kept.</p>
              <span class="admin-card-status">Live</span>
            </a>
            <a class="admin-card" href={localePath('/admin/comments')}>
              <h2>Comments</h2>
              <p>Per-match chat moderation. Walk unreviewed messages one at a time — flagged ones come first. Toggle chat maintenance from here.</p>
              <span class="admin-card-status">Live</span>
            </a>
            <a class="admin-card" href={localePath('/admin/partner-contributions')}>
              <h2>Partner contributions</h2>
              <p>Audit log of every contribution (litres added, article stock added). Cancel committed entries when needed.</p>
              <span class="admin-card-status">Live</span>
            </a>
          </div>

          <SyncUpcomingPanel />
          <TestTournamentPanel />
          <EuropeanFinalsPanel />
          <details style={{ marginTop: 10 }}>
            <summary style={{ cursor: 'pointer', fontSize: 12, color: 'rgba(245,237,224,0.5)' }}>
              Advanced sync actions
            </summary>
            <SyncBootstrapPanel />
            <SyncRefreshPanel />
            <ReresolveBracketPanel />
          </details>

          <a class="admin-back" href={localePath()}>← BeerCup</a>
        </div>
      </div>
    </RequireAdmin>
  );
}

// Primary one-click sync: bootstrap + refresh in sequence. Use this
// for "make our DB match api-football for matches not yet played".
// Re-runnable any time — both steps are idempotent.
function SyncUpcomingPanel() {
  const { accessToken } = useAuth();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<SyncUpcomingResult | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function run() {
    if (!accessToken) return;
    setBusy(true);
    setErr(null);
    setResult(null);
    try {
      setResult(await syncUpcoming(accessToken));
    } catch (e: any) {
      setErr(e?.message || `${e}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section
      style={{
        marginTop: 28,
        padding: 18,
        background: '#120b07',
        border: '1px solid rgba(245,237,224,0.1)',
        borderRadius: 12,
      }}
    >
      <h2 style={{ margin: '0 0 6px', fontSize: 16 }}>Sync upcoming matches</h2>
      <p style={{ margin: '0 0 14px', fontSize: 13, color: 'rgba(245,237,224,0.65)' }}>
        Pulls the api-football catalogue, links any new fixtures or teams
        (e.g. after a KO-round draw), and corrects kickoff + status drift
        across every linked match. Manually-overridden matches are left
        alone. Re-run any time — fully idempotent.
      </p>
      <button class="admin-primary" disabled={busy} onClick={run}>
        {busy ? 'Syncing…' : 'Sync now'}
      </button>
      {err && <p class="admin-error" style={{ marginTop: 12 }}>{err}</p>}
      {result && (
        <div
          style={{
            marginTop: 14,
            padding: 12,
            background: '#0a0705',
            border: '1px solid rgba(245,237,224,0.08)',
            borderRadius: 8,
            fontFamily: 'IBM Plex Mono, Menlo, monospace',
            fontSize: 12,
            color: '#f5ede0',
            whiteSpace: 'pre-wrap',
          }}
        >
          {`teams:    +${result.bootstrap.teams.linked} linked  (${result.bootstrap.teams.unresolvedFromApi} unresolved)\n` +
            `fixtures: +${result.bootstrap.fixtures.linked} linked  ${result.bootstrap.fixtures.skipped} skipped  ${result.bootstrap.fixtures.ambiguous} ambiguous\n` +
            `kickoffs: ${result.refresh.matchesUpdated} updated  (${result.refresh.reqs} api request${result.refresh.reqs === 1 ? '' : 's'})` +
            (result.refresh.errors && result.refresh.errors.length > 0
              ? `\nerrors:\n  ${result.refresh.errors.join('\n  ')}`
              : '')}
        </div>
      )}
    </section>
  );
}

// Inline panel for the one-shot api-football bootstrap. Lives on the
// admin home because it's a rare operator action — most days the
// schedulers handle everything and this panel sits idle. Re-runnable
// after every knockout-round draw to link the new fixtures.
function SyncBootstrapPanel() {
  const { accessToken } = useAuth();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<BootstrapResult | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function run(dry: boolean) {
    if (!accessToken) return;
    setBusy(true);
    setErr(null);
    setResult(null);
    try {
      const r = await bootstrapSync(accessToken, dry);
      setResult(r);
    } catch (e: any) {
      setErr(e?.message || `${e}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section
      style={{
        marginTop: 28,
        padding: 18,
        background: '#120b07',
        border: '1px solid rgba(245,237,224,0.1)',
        borderRadius: 12,
      }}
    >
      <h2 style={{ margin: '0 0 6px', fontSize: 16 }}>Match-sync bootstrap</h2>
      <p style={{ margin: '0 0 14px', fontSize: 13, color: 'rgba(245,237,224,0.65)' }}>
        Links our teams and matches to api-football's catalogue
        (<code style={{ fontFamily: 'IBM Plex Mono, Menlo, monospace' }}>teams.api_team_id</code>{' '}
        and <code style={{ fontFamily: 'IBM Plex Mono, Menlo, monospace' }}>matches.api_fixture_id</code>).
        Idempotent — re-run after each WC round draw to link the new fixtures.
      </p>
      <div style={{ display: 'flex', gap: 10 }}>
        <button class="admin-link" disabled={busy} onClick={() => run(true)}>
          {busy ? 'Running…' : 'Dry-run (preview)'}
        </button>
        <button class="admin-primary" disabled={busy} onClick={() => run(false)}>
          {busy ? 'Running…' : 'Run & write'}
        </button>
      </div>
      {err && <p class="admin-error" style={{ marginTop: 12 }}>{err}</p>}
      {result && (
        <div
          style={{
            marginTop: 14,
            padding: 12,
            background: '#0a0705',
            border: '1px solid rgba(245,237,224,0.08)',
            borderRadius: 8,
            fontFamily: 'IBM Plex Mono, Menlo, monospace',
            fontSize: 12,
            color: '#f5ede0',
            whiteSpace: 'pre-wrap',
          }}
        >
          {`league=${result.leagueId}  season=${result.season}  dryRun=${result.dryRun}\n` +
            `teams:    linked=${result.teams.linked}  unresolved=${result.teams.unresolvedFromApi}\n` +
            `fixtures: linked=${result.fixtures.linked}  skipped=${result.fixtures.skipped}  ambiguous=${result.fixtures.ambiguous}`}
        </div>
      )}
    </section>
  );
}

// Test-tournament panel: enable/disable the Serie A 2025/26 dry-run
// without redeploying, seed the season from api-football in production,
// and force a live tick when you don't want to wait for the next
// Cloud Scheduler minute. The toggle writes to `app_settings.test_
// tournament_code`; the seed + tick + refresh buttons hit the
// admin endpoints under `/api/admin/test-tournament/`.
function TestTournamentPanel() {
  const { accessToken } = useAuth();
  const [settings, setSettings] = useState<AppSettings | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<'toggle' | 'seed' | 'tick' | 'refresh' | 'backfill' | null>(null);
  const [actionErr, setActionErr] = useState<string | null>(null);
  const [lastSeed, setLastSeed] = useState<SeedTestTournamentResult | null>(null);
  const [lastTick, setLastTick] = useState<TestTournamentTickResult | null>(null);
  const [lastRefresh, setLastRefresh] = useState<RefreshSummary | null>(null);
  const [lastBackfill, setLastBackfill] = useState<BackfillTestTournamentResult | null>(null);
  const [backfillDays, setBackfillDays] = useState(2);
  // Tournament code we surface in the toggle. Hard-coded today because
  // there's only one test tournament; trivial to make this a select later.
  const testCode = 'serie-a-2025-26';

  useEffect(() => {
    if (!accessToken) return;
    getAdminSettings(accessToken)
      .then(setSettings)
      .catch((e) => setLoadErr(`${e}`));
  }, [accessToken]);

  const enabled = settings?.testTournamentCode === testCode;

  async function toggle(on: boolean) {
    if (!accessToken) return;
    setBusy('toggle');
    setActionErr(null);
    try {
      const next = await putAdminSettings(accessToken, {
        testTournamentCode: on ? testCode : '',
      });
      setSettings(next);
    } catch (e: any) {
      setActionErr(e?.message || `${e}`);
    } finally {
      setBusy(null);
    }
  }

  async function runSeed(includeDone: boolean) {
    if (!accessToken) return;
    setBusy('seed');
    setActionErr(null);
    setLastSeed(null);
    try {
      setLastSeed(await seedTestTournament(accessToken, { includeDone }));
    } catch (e: any) {
      setActionErr(e?.message || `${e}`);
    } finally {
      setBusy(null);
    }
  }

  async function runTick() {
    if (!accessToken) return;
    setBusy('tick');
    setActionErr(null);
    setLastTick(null);
    try {
      setLastTick(await syncTestTournamentTick(accessToken));
    } catch (e: any) {
      setActionErr(e?.message || `${e}`);
    } finally {
      setBusy(null);
    }
  }

  async function runRefresh() {
    if (!accessToken) return;
    setBusy('refresh');
    setActionErr(null);
    setLastRefresh(null);
    try {
      setLastRefresh(await refreshTestTournamentFixtures(accessToken));
    } catch (e: any) {
      setActionErr(e?.message || `${e}`);
    } finally {
      setBusy(null);
    }
  }

  async function runBackfill() {
    if (!accessToken) return;
    setBusy('backfill');
    setActionErr(null);
    setLastBackfill(null);
    try {
      setLastBackfill(await backfillTestTournament(accessToken, backfillDays));
    } catch (e: any) {
      setActionErr(e?.message || `${e}`);
    } finally {
      setBusy(null);
    }
  }

  return (
    <section
      style={{
        marginTop: 14,
        padding: 18,
        background: '#120b07',
        border: '1px solid rgba(245,237,224,0.1)',
        borderRadius: 12,
      }}
    >
      <h2 style={{ margin: '0 0 6px', fontSize: 16 }}>
        Test tournament — Serie A 2025/26
      </h2>
      <p style={{ margin: '0 0 14px', fontSize: 13, color: 'rgba(245,237,224,0.65)' }}>
        Piggy-backs on the existing api-football + scoring + leaderboard pipeline
        to dry-run a second tournament while WC2026 ramps up. Predictions go to a
        separate leaderboard; auto-pick is OFF for this tournament; voucher pool
        is independent. Surfaced on the mobile Play tab as a "Serie A" section
        when the toggle below is on.
      </p>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <span style={{
          fontFamily: 'IBM Plex Mono, Menlo, monospace',
          fontSize: 12,
          color: enabled ? '#f5ede0' : 'rgba(245,237,224,0.5)',
        }}>
          status: {settings == null ? '…' : enabled ? 'ENABLED' : 'disabled'}
        </span>
        <button
          class="admin-primary"
          disabled={busy !== null || settings === null}
          onClick={() => toggle(!enabled)}
        >
          {busy === 'toggle' ? 'Saving…' : enabled ? 'Disable' : 'Enable'}
        </button>
        {loadErr && <span class="admin-error">{loadErr}</span>}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
        <button class="admin-primary" disabled={busy !== null} onClick={() => runSeed(true)}>
          {busy === 'seed' ? 'Seeding…' : 'Seed from api-football (full)'}
        </button>
        <button class="admin-link" disabled={busy !== null} onClick={() => runSeed(false)}>
          {busy === 'seed' ? '…' : 'Seed (teams + matches only)'}
        </button>
        <button class="admin-link" disabled={busy !== null} onClick={runRefresh}>
          {busy === 'refresh' ? '…' : 'Refresh fixtures'}
        </button>
        <button class="admin-link" disabled={busy !== null} onClick={runTick}>
          {busy === 'tick' ? '…' : 'Run live tick now'}
        </button>
      </div>

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 10,
          alignItems: 'center',
          marginBottom: 12,
          paddingTop: 10,
          borderTop: '1px solid rgba(245,237,224,0.06)',
        }}
      >
        <span style={{ fontSize: 12, color: 'rgba(245,237,224,0.55)' }}>
          Backfill the last
        </span>
        <input
          type="number"
          min={1}
          max={60}
          value={backfillDays}
          onInput={(e) => {
            const v = parseInt((e.target as HTMLInputElement).value, 10);
            if (!Number.isNaN(v) && v > 0 && v <= 60) setBackfillDays(v);
          }}
          style={{
            width: 56,
            padding: '4px 8px',
            background: '#0a0705',
            color: '#f5ede0',
            border: '1px solid rgba(245,237,224,0.15)',
            borderRadius: 6,
            fontFamily: 'IBM Plex Mono, Menlo, monospace',
            fontSize: 12,
          }}
        />
        <span style={{ fontSize: 12, color: 'rgba(245,237,224,0.55)' }}>
          day{backfillDays === 1 ? '' : 's'} of done matches
          (events / stats / lineups only — cheap)
        </span>
        <button class="admin-primary" disabled={busy !== null} onClick={runBackfill}>
          {busy === 'backfill' ? 'Backfilling…' : 'Backfill now'}
        </button>
      </div>

      {actionErr && <p class="admin-error" style={{ marginTop: 12 }}>{actionErr}</p>}

      {lastSeed && (
        <pre style={resultBoxStyle}>
{`league=${lastSeed.leagueId}  season=${lastSeed.season}  code=${lastSeed.tournamentCode}
teams:    +${lastSeed.teamsInserted} inserted  (${lastSeed.teamsTotal} total in tournament)
matches:  ${lastSeed.matchesUpserted} upserted  (${lastSeed.matchesTotal} total)
events:   +${lastSeed.eventsInserted}  upd ${lastSeed.eventsUpdated}  retracted ${lastSeed.eventsRetracted}
stats:    ${lastSeed.statsUpserted} rows
lineups:  ${lastSeed.lineupsFetched} fetched
errors:   ${lastSeed.errors}
budget:   daily=${lastSeed.dailyRemaining}  minute=${lastSeed.minuteRemaining}`}
        </pre>
      )}
      {lastTick && (
        <pre style={resultBoxStyle}>
{`reqs=${lastTick.reqs}  live=${lastTick.liveMatches}  ` +
            `updated=${lastTick.matchesUpdated}  events+${lastTick.eventsInserted}  ` +
            `events_retracted=${lastTick.eventsRetracted}  stats=${lastTick.statsUpdated}\n` +
            (lastTick.skipped ? `skipped: ${lastTick.skipped}\n` : '') +
            (lastTick.errors && lastTick.errors.length > 0
              ? `errors:\n  ${lastTick.errors.join('\n  ')}`
              : '')}
        </pre>
      )}
      {lastRefresh && (
        <pre style={resultBoxStyle}>
{`reqs=${lastRefresh.reqs}  matches_updated=${lastRefresh.matchesUpdated}` +
            (lastRefresh.errors && lastRefresh.errors.length > 0
              ? `\nerrors:\n  ${lastRefresh.errors.join('\n  ')}`
              : '')}
        </pre>
      )}
      {lastBackfill && (
        <pre style={resultBoxStyle}>
{`scanned:  ${lastBackfill.matchesScanned} done matches
events:   +${lastBackfill.eventsInserted}  upd ${lastBackfill.eventsUpdated}  retracted ${lastBackfill.eventsRetracted}
stats:    ${lastBackfill.statsUpserted} rows
lineups:  ${lastBackfill.lineupsFetched} fetched
errors:   ${lastBackfill.errors}
budget:   daily=${lastBackfill.dailyRemaining}  minute=${lastBackfill.minuteRemaining}`}
        </pre>
      )}
    </section>
  );
}

const resultBoxStyle: any = {
  marginTop: 14,
  padding: 12,
  background: '#0a0705',
  border: '1px solid rgba(245,237,224,0.08)',
  borderRadius: 8,
  fontFamily: 'IBM Plex Mono, Menlo, monospace',
  fontSize: 12,
  color: '#f5ede0',
  whiteSpace: 'pre-wrap',
};

// European Finals test tournament panel. Two responsibilities:
//
//   1. Enable / disable surfacing — flips app_settings.test_tournament_code
//      between 'european-finals-2026' and '' (empty). Since that column
//      holds a single string, enabling here implicitly disables the Serie
//      A panel above; only one test tournament is visible to the app at a
//      time. The status pill reflects which (if any) is active.
//
//   2. One-shot api-football linkage — resolves the UCL + UECL finals
//      against api-football (UCL league=2, UECL league=848, season=2025),
//      upserts the real finalist team rows, and stamps api_fixture_id
//      onto the migration's placeholder match rows. After that the
//      per-minute live ticker drives status + score + events automatically.
//      Idempotent — safe to re-run; legs without a final fixture in the
//      date window come back with linked=false.
function EuropeanFinalsPanel() {
  const { accessToken } = useAuth();
  const [settings, setSettings] = useState<AppSettings | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<'toggle' | 'link' | null>(null);
  const [actionErr, setActionErr] = useState<string | null>(null);
  const [result, setResult] = useState<LinkEuropeanFinalsResult | null>(null);
  const testCode = 'european-finals-2026';

  useEffect(() => {
    if (!accessToken) return;
    getAdminSettings(accessToken)
      .then(setSettings)
      .catch((e) => setLoadErr(`${e}`));
  }, [accessToken]);

  const enabled = settings?.testTournamentCode === testCode;

  async function toggle(on: boolean) {
    if (!accessToken) return;
    setBusy('toggle');
    setActionErr(null);
    try {
      const next = await putAdminSettings(accessToken, {
        testTournamentCode: on ? testCode : '',
      });
      setSettings(next);
    } catch (e: any) {
      setActionErr(e?.message || `${e}`);
    } finally {
      setBusy(null);
    }
  }

  async function runLink() {
    if (!accessToken) return;
    setBusy('link');
    setActionErr(null);
    setResult(null);
    try {
      setResult(await linkEuropeanFinals(accessToken));
    } catch (e: any) {
      setActionErr(e?.message || `${e}`);
    } finally {
      setBusy(null);
    }
  }

  return (
    <section
      style={{
        marginTop: 14,
        padding: 18,
        background: '#120b07',
        border: '1px solid rgba(245,237,224,0.1)',
        borderRadius: 12,
      }}
    >
      <h2 style={{ margin: '0 0 6px', fontSize: 16 }}>
        Test tournament — European Finals 2026
      </h2>
      <p style={{ margin: '0 0 14px', fontSize: 13, color: 'rgba(245,237,224,0.65)' }}>
        UEFA Conference League final (league 848) + UEFA Champions League
        final (league 2) for season 2025. Enabling surfaces both matches as
        a second section on the Play tab and a tournament toggle on the
        Boards tab. Only one test tournament can be active at a time —
        enabling here disables Serie A above.
      </p>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <span style={{
          fontFamily: 'IBM Plex Mono, Menlo, monospace',
          fontSize: 12,
          color: enabled ? '#f5ede0' : 'rgba(245,237,224,0.5)',
        }}>
          status: {settings == null ? '…' : enabled ? 'ENABLED' : 'disabled'}
        </span>
        <button
          class="admin-primary"
          disabled={busy !== null || settings === null}
          onClick={() => toggle(!enabled)}
        >
          {busy === 'toggle' ? 'Saving…' : enabled ? 'Disable' : 'Enable'}
        </button>
        {loadErr && <span class="admin-error">{loadErr}</span>}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
        <button class="admin-primary" disabled={busy !== null} onClick={runLink}>
          {busy === 'link' ? 'Linking…' : 'Link finals to api-football'}
        </button>
        <span style={{ alignSelf: 'center', fontSize: 12, color: 'rgba(245,237,224,0.55)' }}>
          One-shot — resolves the real finalist teams + fixture ids. Safe to re-run.
        </span>
      </div>

      {actionErr && <p class="admin-error" style={{ marginTop: 12 }}>{actionErr}</p>}
      {result && (
        <pre style={resultBoxStyle}>
{Object.entries(result.finals)
            .sort(([a], [b]) => Number(a) - Number(b))
            .map(([n, leg]) =>
              leg.linked
                ? `match #${n} (league ${leg.leagueId}): linked\n` +
                  `  fixture=${leg.apiFixtureId}\n` +
                  `  ${leg.homeName} (api=${leg.apiTeamHomeId}) vs ${leg.awayName} (api=${leg.apiTeamAwayId})\n` +
                  `  kickoff=${leg.kickoffUtc}`
                : `match #${n} (league ${leg.leagueId}): NOT linked — ${leg.error ?? 'no fixture in window'}`,
            )
            .join('\n\n')}
        </pre>
      )}
    </section>
  );
}

// Realign-now: pulls every linked WC2026 fixture from api-football and
// corrects kickoff / status drift in one shot. The every-30-min cron
// does the same in a rolling 48h window — this is the operator's
// "don't make me wait" button.
function ReresolveBracketPanel() {
  const { accessToken } = useAuth();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ReresolveBracketResult | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function run() {
    if (!accessToken) return;
    setBusy(true);
    setErr(null);
    setResult(null);
    try {
      setResult(await reresolveBracket(accessToken));
    } catch (e: any) {
      setErr(e?.message || `${e}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section
      style={{
        marginTop: 14,
        padding: 18,
        background: '#120b07',
        border: '1px solid rgba(245,237,224,0.1)',
        borderRadius: 12,
      }}
    >
      <h2 style={{ margin: '0 0 6px', fontSize: 16 }}>Rebuild knockout bracket</h2>
      <p style={{ margin: '0 0 14px', fontSize: 13, color: 'rgba(245,237,224,0.65)' }}>
        Clears every not-yet-played R16+ slot and re-fills it from the corrected
        bracket pairing. Use this once after a bracket fix when the wrong teams
        are already showing (e.g. R16 read "Canada vs Brazil" instead of "Canada
        vs Morocco"). Manually-overridden and already-played matches are left
        alone. Run "Sync upcoming" afterwards to re-link kickoff times.
      </p>
      <button class="admin-primary" disabled={busy} onClick={run}>
        {busy ? 'Rebuilding…' : 'Rebuild bracket'}
      </button>
      {err && <p class="admin-error" style={{ marginTop: 12 }}>{err}</p>}
      {result && (
        <div
          style={{
            marginTop: 14,
            padding: 12,
            background: '#0a0705',
            border: '1px solid rgba(245,237,224,0.08)',
            borderRadius: 8,
            fontFamily: 'IBM Plex Mono, Menlo, monospace',
            fontSize: 12,
            color: '#f5ede0',
            whiteSpace: 'pre-wrap',
          }}
        >
          {`slots_cleared=${result.cleared}  slots_refilled=${result.refilled}  stale_links_broken=${result.unlinked}`}
        </div>
      )}
    </section>
  );
}

function SyncRefreshPanel() {
  const { accessToken } = useAuth();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<RefreshSummary | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function run() {
    if (!accessToken) return;
    setBusy(true);
    setErr(null);
    setResult(null);
    try {
      const r = await refreshFixtures(accessToken);
      setResult(r);
    } catch (e: any) {
      setErr(e?.message || `${e}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section
      style={{
        marginTop: 14,
        padding: 18,
        background: '#120b07',
        border: '1px solid rgba(245,237,224,0.1)',
        borderRadius: 12,
      }}
    >
      <h2 style={{ margin: '0 0 6px', fontSize: 16 }}>Realign fixtures now</h2>
      <p style={{ margin: '0 0 14px', fontSize: 13, color: 'rgba(245,237,224,0.65)' }}>
        Pulls every linked WC2026 fixture from api-football and corrects kickoff drift +
        status (postponed / cancelled / rescheduled). Skips manually-overridden matches.
        Team pairings aren't touched — those come from the slot resolver. The 30-min cron
        does the same automatically; this is for when you don't want to wait.
      </p>
      <button class="admin-primary" disabled={busy} onClick={run}>
        {busy ? 'Refreshing…' : 'Refresh now'}
      </button>
      {err && <p class="admin-error" style={{ marginTop: 12 }}>{err}</p>}
      {result && (
        <div
          style={{
            marginTop: 14,
            padding: 12,
            background: '#0a0705',
            border: '1px solid rgba(245,237,224,0.08)',
            borderRadius: 8,
            fontFamily: 'IBM Plex Mono, Menlo, monospace',
            fontSize: 12,
            color: '#f5ede0',
            whiteSpace: 'pre-wrap',
          }}
        >
          {`requests=${result.reqs}  matches_updated=${result.matchesUpdated}` +
            (result.errors && result.errors.length > 0
              ? `\nerrors:\n  ${result.errors.join('\n  ')}`
              : '')}
        </div>
      )}
    </section>
  );
}
